import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navLinks } from "@/mocks/site";

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled || menuOpen || searchOpen;
  const tone = solid ? "text-foreground-950" : "text-background-50";
  const subTone = solid ? "text-foreground-600" : "text-background-100/80";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/events?q=${encodeURIComponent(query)}` : "/events");
    setSearchOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "border-b border-background-200 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="吃瓜复盘局首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flashlight-fill text-lg"></i>
          </span>
          <span className={`flex flex-col leading-none ${tone}`}>
            <span className="font-heading text-lg font-bold tracking-tight">吃瓜复盘局</span>
            <span className={`mt-0.5 text-[10px] tracking-wide ${subTone}`}>
              网红事件 · 完整经过
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="主导航">
          {navLinks.map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              className={({ isActive }) =>
                `relative py-1 text-sm transition-colors ${
                  isActive
                    ? solid
                      ? "font-medium text-primary-600"
                      : "font-medium text-background-50"
                    : solid
                      ? "text-foreground-700 hover:text-primary-600"
                      : "text-background-100/85 hover:text-background-50"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {link.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-primary-500 transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            aria-label="搜索事件"
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors ${
              solid
                ? "text-foreground-700 hover:bg-background-200"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className="ri-search-line text-lg"></i>
          </button>
          <Link
            to="/#subscribe"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-block"
          >
            订阅更新
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-md transition-colors md:hidden ${
              solid
                ? "text-foreground-950 hover:bg-background-200"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-b border-background-200 bg-background-50 px-4 pb-4 md:px-8">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件、人物或关键词，如：带货翻车"
                className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <button
              type="submit"
              className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <nav
          className="border-b border-background-200 bg-background-50 px-4 pb-5 pt-2 md:hidden"
          aria-label="移动端导航"
        >
          <ul className="flex flex-col">
            {navLinks.map((link) => (
              <li key={link.href}>
                <NavLink
                  to={link.href}
                  className={({ isActive }) =>
                    `block border-b border-background-100 py-3 text-base ${
                      isActive ? "font-medium text-primary-600" : "text-foreground-800"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <Link
            to="/#subscribe"
            className="mt-4 block whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50"
          >
            订阅事件更新
          </Link>
        </nav>
      )}
    </header>
  );
}